import React from 'react';
import { AgendaItem, AgendaType } from '@/types';
import { countByType, daysUntilNextHigh, eventsInMonth } from '@/lib/dateUtils';
import { MiniCalendar } from './MiniCalendar';
import { TONE_DOT, TYPE_LABEL } from './EventPill';

interface Props {
  cursor: Date;
  events: AgendaItem[];
  filterTypes: Record<AgendaType, boolean>;
  setFilterTypes: React.Dispatch<React.SetStateAction<Record<AgendaType, boolean>>>;
  byDay: Record<string, AgendaItem[]>;
  onCursorChange: (d: Date) => void;
  onDayClick?: (d: Date) => void;
}

const TYPE_KEYS: AgendaType[] = ['edital', 'certidao', 'contrato', 'sessao'];

export function AgendaEditorialHeader({
  cursor,
  events,
  filterTypes,
  setFilterTypes,
  byDay,
  onCursorChange,
  onDayClick,
}: Props) {
  const monthLabel = cursor.toLocaleDateString('pt-BR', { month: 'long' });
  const year = cursor.getFullYear();
  const monthEvents = eventsInMonth(events, cursor);
  const stats = countByType(monthEvents);
  const nextCritical = daysUntilNextHigh(events);

  return (
    <div>
      {/* Headline editorial */}
      <h1
        className="text-[84px] leading-[0.92] tracking-[-0.04em] font-extralight m-0 text-ink-title dark:text-ink-title-dark"
        style={{ fontWeight: 200 }}
      >
        <span className="block capitalize">{monthLabel}</span>
        <span className="block text-ink-placeholder dark:text-ink-placeholder-dark">{year}</span>
      </h1>

      {/* Régua 40×1px */}
      <div className="w-10 h-px bg-ink-title/60 dark:bg-ink-title-dark/60 my-3.5" />

      {/* Stats compactos */}
      <div className="text-[12px] leading-[1.55] text-ink-subtitle dark:text-ink-subtitle-dark space-y-0.5">
        <div>
          <b className="text-ink-title dark:text-ink-title-dark tabular-nums font-medium">
            {monthEvents.length}
          </b>{' '}
          compromisso{monthEvents.length === 1 ? '' : 's'} este mês
        </div>
        {nextCritical !== null && (
          <div>
            <b className="text-ink-title dark:text-ink-title-dark tabular-nums font-medium">
              {nextCritical}
            </b>{' '}
            dia{nextCritical === 1 ? '' : 's'} até o próximo prazo crítico
          </div>
        )}
      </div>

      {/* Filtros por tipo */}
      <div className="mt-6 flex flex-col gap-0">
        {TYPE_KEYS.map(k => {
          const enabled = filterTypes[k];
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilterTypes(f => ({ ...f, [k]: !f[k] }))}
              className={[
                'flex items-center justify-between py-2 border-b border-line dark:border-line-dark text-[12px] transition-opacity',
                enabled ? 'opacity-100' : 'opacity-40',
              ].join(' ')}
              aria-pressed={enabled}
            >
              <span className="inline-flex items-center gap-2 text-ink-text dark:text-ink-text-dark">
                <span className={`w-1.5 h-1.5 rounded-full ${TONE_DOT[k]}`} />
                {TYPE_LABEL[k]}
              </span>
              <span className="text-ink-title dark:text-ink-title-dark font-medium tabular-nums">
                {stats[k]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Mini-calendário */}
      <div className="mt-5">
        <MiniCalendar
          cursor={cursor}
          byDay={byDay}
          onCursorChange={onCursorChange}
          onDayClick={onDayClick}
        />
      </div>
    </div>
  );
}
